import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2 } from 'lucide-react';
import { fetchUrlImages } from '../../api/posts/fetch-post.api';

export interface CreativeFields {
  title?: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl?: string;
  ctaText?: string;
}

interface Props {
  value: CreativeFields;
  onChange: (patch: Partial<CreativeFields>) => void;
  /** Hiện ô link đích (form thay nội dung cần, form tạo chiến dịch đã có ô riêng). */
  showLanding?: boolean;
  disabled?: boolean;
}

const inputCls =
  'mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text';

/** Phần nhập nội dung quảng cáo dùng chung: tiêu đề, mô tả, ảnh, video đính kèm, nút kêu gọi. */
const AdCreativeForm: React.FC<Props> = ({ value, onChange, showLanding, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const lock = useRef(false);

  const onUpload = async (file?: File) => {
    if (!file || lock.current) return;
    lock.current = true;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('type', file);
      const res = await fetchUrlImages(fd);
      if (res?.length) onChange({ imageUrl: res[0].url });
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      lock.current = false;
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {showLanding && (
        <label className="text-sm ui-text block">
          Link đích (khi bấm quảng cáo)
          <input
            className={inputCls}
            value={value.landingUrl || ''}
            onChange={(e) => onChange({ landingUrl: e.target.value })}
            placeholder="https://..."
            disabled={disabled}
          />
        </label>
      )}
      <label className="text-sm ui-text block">
        Tiêu đề quảng cáo
        <input
          className={inputCls}
          value={value.title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Tiêu đề hấp dẫn"
          disabled={disabled}
        />
      </label>
      <label className="text-sm ui-text block">
        Mô tả ngắn
        <textarea
          className={`${inputCls} resize-none`}
          rows={2}
          value={value.body || ''}
          onChange={(e) => onChange({ body: e.target.value })}
          disabled={disabled}
        />
      </label>
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm ui-text cursor-pointer rounded-md ui-divider border px-3 py-2">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          Ảnh/banner
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={disabled || uploading}
            onChange={(e) => {
              onUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {value.imageUrl ? (
          <img src={value.imageUrl} alt="preview" className="h-12 rounded-md object-cover" />
        ) : null}
      </div>

      {value.videoUrl ? (
        <div className="flex items-center gap-3 rounded-md ui-divider border p-2">
          <video src={value.videoUrl} className="h-16 rounded-md bg-black object-cover" muted />
          <span className="text-xs ui-muted flex-1">Video từ Cab Studio đã đính kèm.</span>
          <button
            type="button"
            onClick={() => onChange({ videoUrl: '' })}
            className="text-xs text-rose-500 hover:underline"
          >
            Bỏ
          </button>
        </div>
      ) : null}

      <label className="text-sm ui-text block">
        Nút kêu gọi
        <input
          className={inputCls}
          value={value.ctaText || ''}
          onChange={(e) => onChange({ ctaText: e.target.value })}
          placeholder="VD: Tìm hiểu thêm"
          disabled={disabled}
        />
      </label>
    </div>
  );
};

export default AdCreativeForm;
